import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/PageHeader';
import usePageSearchIndex from '../hooks/usePageSearchIndex';
import './Grievance.css';

const Grievance = () => {
  const { isMarathi } = useLanguage();

  usePageSearchIndex({
    mr: ['तक्रार निवारण', 'तक्रार', 'निवारण', 'अर्ज'],
    en: ['grievance', 'complaint', 'redressal', 'application']
  });

  const [formData, setFormData] = useState({
    fullName: '',
    contactNumber: '',
    email: '',
    feedbackType: 'grievance',
    message: '',
    attachment: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({
    fullName: '',
    contactNumber: '',
    email: '',
    message: ''
  });

  const pageHeaderData = {
    title: {
      marathi: 'तक्रार निवारण',
      english: 'Grievance Redressal'
    },
    subtitle: {
      marathi: 'आपली तक्रार साझा करा',
      english: 'Share your grievance'
    }
  };

  // Validate individual field
  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'fullName':
        if (!value || !value.trim()) {
          error = isMarathi() ? 'पूर्ण नाव आवश्यक आहे' : 'Full name is required';
        }
        break;
      case 'contactNumber':
        const phoneRegex = /^[0-9]{10}$/;
        if (!value || !value.trim()) {
          error = isMarathi() ? 'संपर्क क्रमांक आवश्यक आहे' : 'Contact number is required';
        } else if (!phoneRegex.test(value.replace(/\D/g, ''))) {
          error = isMarathi() ? 'कृपया वैध 10-अंकी मोबाइल नंबर प्रविष्ट करा' : 'Please enter a valid 10-digit mobile number';
        }
        break;
      case 'email':
        if (!value || !value.trim()) {
          error = isMarathi() ? 'ईमेल पत्ता आवश्यक आहे' : 'Email address is required';
        } else {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            error = isMarathi() ? 'कृपया वैध ईमेल पत्ता प्रविष्ट करा' : 'Please enter a valid email address';
          }
        }
        break;
      case 'message':
        if (!value || !value.trim()) {
          error = isMarathi() ? 'संदेश आवश्यक आहे' : 'Message is required';
        }
        break;
      default:
        break;
    }
    
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
    
    return !error;
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      const file = files[0];
      const MAX = 5 * 1024 * 1024; // 5 MB
      if (file && file.size > MAX) {
        setSubmitStatus({
          type: 'error',
          message: isMarathi()
            ? 'कमाल फाइल आकार ५ MB आहे.'
            : 'Maximum file size is 5 MB.'
        });
        e.target.value = '';
        return;
      }
    }
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
    
    // Validate field on change (for fields that have been touched)
    if (name !== 'attachment') {
      validateField(name, value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Validate all fields
    const isFullNameValid = validateField('fullName', formData.fullName);
    const isContactValid = validateField('contactNumber', formData.contactNumber);
    const isEmailValid = validateField('email', formData.email);
    const isMessageValid = validateField('message', formData.message);

    if (!isFullNameValid || !isContactValid || !isEmailValid || !isMessageValid) {
      setSubmitStatus({
        type: 'error',
        message: isMarathi() 
          ? 'कृपया सर्व आवश्यक फील्ड भरा आणि त्रुटी दुरुस्त करा' 
          : 'Please fill all required fields and fix errors'
      });
      setIsSubmitting(false);
      return;
    }

    try {

      // Build and send multipart/form-data to Email API
      const API_BASE = process.env.NODE_ENV === 'production' 
        ? 'https://adminconsole.mlwb.in/api' 
        : 'http://localhost:5000/api';
      const apiUrl = `${API_BASE}/email`;
      const data = new FormData();
      data.append('name', formData.fullName);
      data.append('contact', formData.contactNumber);
      data.append('email', formData.email);
      data.append('type', 'grievance');
      data.append('message', formData.message);
      if (formData.attachment) data.append('attachment', formData.attachment);

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: data
      });

      if (!response.ok) {
        const resultText = await response.text().catch(() => '');
        throw new Error(resultText || 'Failed to send grievance');
      }

      setSubmitStatus({
        type: 'success',
        message: isMarathi() 
          ? 'आपली तक्रार यशस्वीरित्या सबमिट केली गेली. धन्यवाद!' 
          : 'Your grievance has been submitted successfully. Thank you!'
      });

      // Reset form
      setFormData({
        fullName: '',
        contactNumber: '',
        email: '',
        feedbackType: 'grievance',
        message: '',
        attachment: null
      });
      setFieldErrors({
        fullName: '',
        contactNumber: '',
        email: '',
        message: ''
      });

    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: isMarathi() 
          ? 'काही त्रुटी आली. कृपया पुन्हा प्रयत्न करा.' 
          : 'An error occurred. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Live validation to control submit button
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email || '');
  const isPhoneValid = /^[0-9]{10}$/.test(String(formData.contactNumber || '').replace(/\D/g, ''));
  const isRequiredFilled =
    (formData.fullName || '').trim() &&
    (formData.contactNumber || '').trim() &&
    (formData.email || '').trim() &&
    (formData.message || '').trim();
  const isFormValid = Boolean(isRequiredFilled && isEmailValid && isPhoneValid);

  return (
    <div className="grievance-container">
        {/* Page Header */}
        <PageHeader 
          title={pageHeaderData.title}
          subtitle={pageHeaderData.subtitle}
        />

        {/* Main Content */}
        <div className="grievance-content" data-read-section="grievance-content">
          {/* Grievance Form */}
          <section className="grievance-section">
            <h2 className="section-title">
              {isMarathi() ? 'तक्रार फॉर्म' : 'Grievance Form'}
            </h2>
            
            <form className="grievance-form" onSubmit={handleSubmit}>
              {/* Full Name */}
              <div className="form-group">
                <label htmlFor="fullName" className="form-label">
                  {isMarathi() ? 'पूर्ण नाव' : 'Full Name'} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={`form-input ${fieldErrors.fullName ? 'error' : ''}`}
                  placeholder={isMarathi() ? 'आपले पूर्ण नाव प्रविष्ट करा' : 'Enter your full name'}
                  aria-invalid={!!fieldErrors.fullName}
                  aria-describedby={fieldErrors.fullName ? 'fullName-error' : undefined}
                  required
                />
                {fieldErrors.fullName && (
                  <span id="fullName-error" role="alert" className="field-error">
                    {fieldErrors.fullName}
                  </span>
                )}
              </div>

              {/* Contact Number */}
              <div className="form-group">
                <label htmlFor="contactNumber" className="form-label">
                  {isMarathi() ? 'संपर्क क्रमांक (मोबाइल)' : 'Contact Number (Mobile)'} <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  className={`form-input ${fieldErrors.contactNumber ? 'error' : ''}`}
                  placeholder={isMarathi() ? '10-अंकी मोबाइल नंबर' : '10-digit mobile number'}
                  maxLength="10"
                  aria-invalid={!!fieldErrors.contactNumber}
                  aria-describedby={fieldErrors.contactNumber ? 'contactNumber-error' : undefined}
                  required
                />
                {fieldErrors.contactNumber && (
                  <span id="contactNumber-error" role="alert" className="field-error">
                    {fieldErrors.contactNumber}
                  </span>
                )}
              </div>

              {/* Email Address */}
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  {isMarathi() ? 'ईमेल पत्ता' : 'Email Address'} <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-input ${fieldErrors.email ? 'error' : ''}`}
                  placeholder={isMarathi() ? 'आपला ईमेल पत्ता' : 'Your email address'}
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                  required
                />
                {fieldErrors.email && (
                  <span id="email-error" role="alert" className="field-error">
                    {fieldErrors.email}
                  </span>
                )}
              </div>

              {/* Grievance Type - Grayed Out */}
              <div className="form-group">
                <label htmlFor="feedbackType" className="form-label">
                  {isMarathi() ? 'तक्रार प्रकार' : 'Grievance Type'}
                </label>
                <input
                  type="text"
                  id="feedbackType"
                  name="feedbackType"
                  value={isMarathi() ? 'तक्रार' : 'Grievance'}
                  className="form-input disabled"
                  disabled
                  readOnly
                />
              </div>

              {/* Message */}
              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  {isMarathi() ? 'संदेश' : 'Message'} <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className={`form-textarea ${fieldErrors.message ? 'error' : ''}`}
                  placeholder={isMarathi() ? 'आपली तक्रार येथे लिहा...' : 'Write your grievance here...'}
                  rows="6"
                  aria-invalid={!!fieldErrors.message}
                  aria-describedby={fieldErrors.message ? 'message-error' : undefined}
                  required
                />
                {fieldErrors.message && (
                  <span id="message-error" role="alert" className="field-error">
                    {fieldErrors.message}
                  </span>
                )}
              </div>

              {/* File Attachment */}
              <div className="form-group">
                <label htmlFor="attachment" className="form-label">
                  {isMarathi() ? 'संलग्नक जोडा' : 'Add Attachment'} <span className="optional">({isMarathi() ? 'पर्यायी' : 'Optional'})</span>
                </label>
                <input
                  type="file"
                  id="attachment"
                  name="attachment"
                  onChange={handleInputChange}
                  className="form-file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                />
                <div className="file-info">
                  <p className="file-info-text">
                    <strong>{isMarathi() ? 'स्वीकार्य फाइल स्वरूप:' : 'Accepted file formats:'}</strong> PDF, DOC, DOCX, JPG, JPEG, PNG, GIF
                  </p>
                  <p className="file-info-text">
                    <strong>{isMarathi() ? 'कमाल फाइल आकार:' : 'Maximum file size:'}</strong> 5 MB
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="form-group">
                <button
                  type="submit"
                  className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
                  disabled={!isFormValid || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      {isMarathi() ? 'सबमिट करत आहे...' : 'Submitting...'}
                    </>
                  ) : (
                    isMarathi() ? 'सबमिट करा' : 'Submit'
                  )}
                </button>
              </div>

              {/* Status Message */}
              {submitStatus && (
                <div 
                  role="alert" 
                  aria-live={submitStatus.type === 'error' ? 'assertive' : 'polite'}
                  aria-atomic="true"
                  className={`status-message ${submitStatus.type}`}
                >
                  <div className="status-icon">
                    {submitStatus.type === 'success' ? '✅' : '❌'}
                  </div>
                  <p className="status-text">{submitStatus.message}</p>
                </div>
              )}
            </form>
          </section>

          {/* Additional Information */}
          <section className="grievance-section">
            <h2 className="section-title">
              {isMarathi() ? 'महत्वाची माहिती' : 'Important Information'}
            </h2>
            <div className="info-content">
              <div className="info-item">
                <div className="info-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14,2 14,8 20,8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10,9 9,9 8,9"/>
                  </svg>
                </div>
                <div className="info-details">
                  <h3 className="info-title">
                    {isMarathi() ? 'तक्रार निवारण प्रक्रिया' : 'Grievance Redressal Process'}
                  </h3>
                  <p className="info-text">
                    {isMarathi() 
                      ? 'आपली तक्रार प्राप्त झाल्यानंतर, आम्ही 3-5 कामकाजाच्या दिवसांत आपल्याशी संपर्क साधू.' 
                      : 'After receiving your grievance, we will contact you within 3-5 working days.'
                    }
                  </p>
                </div>
              </div>
              
              <div className="info-item">
                <div className="info-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <circle cx="12" cy="16" r="1"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <div className="info-details">
                  <h3 className="info-title">
                    {isMarathi() ? 'गोपनीयता' : 'Privacy'}
                  </h3>
                  <p className="info-text">
                    {isMarathi() 
                      ? 'आपली व्यक्तिगत माहिती गोपनीय राखली जाईल आणि केवळ तक्रार निवारण प्रक्रियेसाठी वापरली जाईल.' 
                      : 'Your personal information will be kept confidential and used only for the grievance redressal process.'
                    }
                  </p>
                </div>
              </div>
              
              <div className="info-item">
                <div className="info-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <div className="info-details">
                  <h3 className="info-title">
                    {isMarathi() ? 'तातडीची सहायता' : 'Urgent Assistance'}
                  </h3>
                  <p className="info-text">
                    {isMarathi() 
                      ? 'तातडीच्या प्रकरणांसाठी, कृपया थेट आमच्या कार्यालयाशी संपर्क साधा.' 
                      : 'For urgent matters, please contact our office directly.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
    </div>
  );
};

export default Grievance;
